<script setup lang="ts">
  defineOptions({ name: 'EventsListSection' })

  import { computed, ref, type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'

  import CPButton from '@ui/components/CPButton.vue'
  import CPTabPane from '@ui/components/CPTabPane.vue'
  import CPTabs from '@ui/components/CPTabs.vue'

  import EventCard from '../molecules/EventCard.vue'
  import EventCardSkeleton from '../molecules/EventCardSkeleton.vue'
  import SectionEmptyState from '../molecules/SectionEmptyState.vue'
  import type { EventsHubCard, EventsHubListDensity, EventsHubListTab, EventsHubResourceState } from '../../types/events-hub'

  const props = defineProps({
    upcomingState: {
      type: String as PropType<EventsHubResourceState>,
      required: true,
    },
    upcomingEvents: {
      type: Array as PropType<readonly EventsHubCard[]>,
      default: () => [],
    },
    pastState: {
      type: String as PropType<EventsHubResourceState>,
      required: true,
    },
    pastEvents: {
      type: Array as PropType<readonly EventsHubCard[]>,
      default: () => [],
    },
    upcomingTotal: {
      type: Number,
      default: undefined,
    },
    pastTotal: {
      type: Number,
      default: undefined,
    },
    itemLimit: {
      type: Number,
      default: 6,
    },
    density: {
      type: String as PropType<EventsHubListDensity>,
      default: 'comfortable',
    },
    idPrefix: {
      type: String,
      default: 'events-list',
    },
  })

  const emit = defineEmits<{
    openEvent: [eventId: string]
    seeAll: [tab: EventsHubListTab]
  }>()

  const { t } = useI18n()
  const SKELETON_CARD_COUNT = 3
  const activeTab = ref<EventsHubListTab>('upcoming')

  const activeState = computed(() => (activeTab.value === 'upcoming' ? props.upcomingState : props.pastState))
  const activeEvents = computed(() => (activeTab.value === 'upcoming' ? props.upcomingEvents : props.pastEvents))
  const previewEvents = computed(() => activeEvents.value.slice(0, props.itemLimit))
  const upcomingTotalCount = computed(() => props.upcomingTotal ?? props.upcomingEvents.length)
  const pastTotalCount = computed(() => props.pastTotal ?? props.pastEvents.length)
  const activeTotal = computed(() => (activeTab.value === 'upcoming' ? upcomingTotalCount.value : pastTotalCount.value))
  const showSeeAll = computed(() => activeTotal.value > props.itemLimit)
  const gapClass = computed(() => {
    const map = { compact: 'gap-2', comfortable: 'gap-4', roomy: 'gap-6' } as const
    return map[props.density]
  })

  const selectTab = (tab: string | number) => {
    if (tab !== 'upcoming' && tab !== 'past') return
    activeTab.value = tab
  }

  const handleOpenEvent = (eventId: string) => {
    emit('openEvent', eventId)
  }

  const handleSeeAll = () => {
    emit('seeAll', activeTab.value)
  }
</script>

<template>
  <section :id="createElementId(idPrefix, 'section', 'root')" class="flex w-full flex-col" :class="gapClass">
    <div :id="createElementId(idPrefix, 'section-header', 'root')" class="flex items-center justify-between gap-3">
      <div :id="createElementId(idPrefix, 'section-header', 'tabs-wrap')" class="events-list-tabs-wrap">
        <CPTabs
          :id="createElementId(idPrefix, 'section-header', 'tabs')"
          type="segment"
          size="md"
          no-border
          theme="neutral"
          :value="activeTab"
          :max-visible-tabs="2"
          :aria-label="t('clientportal.events.listTabs')"
          @update:value="selectTab"
        >
          <CPTabPane
            name="upcoming"
            :tab="t('clientportal.events.upcoming')"
            tab-key="upcoming"
            :tab-props="{ id: createElementId(idPrefix, 'section-header', 'upcoming') }"
          />
          <CPTabPane
            name="past"
            :tab="t('clientportal.events.past')"
            tab-key="past"
            :tab-props="{ id: createElementId(idPrefix, 'section-header', 'past') }"
          />
        </CPTabs>
      </div>

      <CPButton
        v-if="showSeeAll"
        :id="createElementId(idPrefix, 'section-header', 'see-all')"
        color="primary"
        type="text"
        size="sm"
        @click="handleSeeAll"
      >
        {{ t('clientportal.events.seeAll') }}
      </CPButton>
    </div>

    <div
      v-if="activeState === 'loading'"
      :id="createElementId(idPrefix, 'section', 'loading-grid')"
      class="grid grid-cols-[repeat(auto-fill,minmax(min(100%,400px),400px))] gap-4"
      role="status"
      :aria-label="t('clientportal.events.loadingCardsAriaLabel')"
    >
      <EventCardSkeleton v-for="index in SKELETON_CARD_COUNT" :key="index" :id-prefix="idPrefix" :index="index" />
    </div>

    <SectionEmptyState
      v-else-if="activeState === 'error'"
      :id="createElementId(idPrefix, 'empty-state', 'error')"
      :title="t('clientportal.events.emptyState.loadErrorTitle')"
      :description="t('clientportal.events.emptyState.loadErrorDescription')"
    />

    <SectionEmptyState
      v-else-if="activeState === 'empty' || previewEvents.length === 0"
      :id="createElementId(idPrefix, 'empty-state', 'empty')"
      :title="t('clientportal.events.emptyState.noEventsTitle')"
      :description="t('clientportal.events.emptyState.noEventsDescription')"
    />

    <div
      v-else
      :id="createElementId(idPrefix, 'section', 'content-grid')"
      class="grid grid-cols-[repeat(auto-fill,minmax(min(100%,400px),400px))] gap-4"
    >
      <EventCard
        v-for="(event, index) in previewEvents"
        :key="event.id"
        :event="event"
        :id-prefix="idPrefix"
        :priority="index === 0"
        @open-event="handleOpenEvent"
      />
    </div>
  </section>
</template>

<style scoped>
  .events-list-tabs-wrap :deep(.hr-tabs-rail) {
    border: 1px solid var(--cp-border-600) !important;
    padding: 4px !important;
  }
  .events-list-tabs-wrap :deep(.hr-tabs-tab-wrapper),
  .events-list-tabs-wrap :deep(.hr-tabs-tab) {
    width: 8rem !important;
  }
  .events-list-tabs-wrap,
  .events-list-tabs-wrap :deep(.hr-tabs-rail) {
    min-width: 16rem !important;
    min-height: 2rem !important;
  }
</style>
